import React, { useEffect, useState } from "react";
import { api, formatINR, formatApiError } from "@/lib/api";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", category: "tops", gender: "unisex", price: 0, mrp: 0,
  images: [""], tags: [], fit: "regular", sizes: ["S","M","L","XL"], colors: ["Black"],
  stock: 25, is_drop: false, description: "",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products", { params: { limit: 200 } }).then((r) => setProducts(r.data.products));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...editing,
        price: Number(editing.price),
        mrp: Number(editing.mrp),
        stock: Number(editing.stock),
        images: editing.images.filter(Boolean),
        sizes: editing.sizes.filter(Boolean),
        colors: editing.colors.filter(Boolean),
        tags: editing.tags || [],
      };
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
      } else {
        await api.post("/admin/products", payload);
      }
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-8" data-testid="admin-products-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="font-mono text-xs text-[#FF3B30] tracking-widest">CATALOG</div>
          <h1 className="font-display text-5xl tracking-tighter mt-2">PRODUCTS</h1>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="bg-[#FF3B30] text-white px-6 py-3 font-display tracking-widest text-xs hover:bg-black inline-flex items-center gap-2" data-testid="admin-add-product">
          <Plus size={14} /> NEW PRODUCT
        </button>
      </div>

      <div className="border border-black/10 bg-white">
        <table className="w-full text-sm">
          <thead className="font-display tracking-widest text-xs text-black/50">
            <tr className="border-b border-black/10">
              <th className="text-left p-3">PRODUCT</th>
              <th className="text-left p-3">CATEGORY</th>
              <th className="text-left p-3">GENDER</th>
              <th className="text-right p-3">PRICE</th>
              <th className="text-right p-3">STOCK</th>
              <th className="text-right p-3">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-black/5">
                <td className="p-3 flex items-center gap-3">
                  <img src={p.images?.[0]} alt="" className="w-10 h-12 object-cover" />
                  <span className="font-display tracking-tight">{p.name}</span>
                </td>
                <td className="p-3 text-black/70">{p.category}</td>
                <td className="p-3 text-black/70">{p.gender}</td>
                <td className="p-3 text-right font-display">{formatINR(p.price)}</td>
                <td className="p-3 text-right">{p.stock}</td>
                <td className="p-3 text-right space-x-2">
                  <button onClick={() => setEditing(p)} className="hover:text-[#FF3B30]" data-testid={`admin-edit-${p.id}`}><Edit3 size={14} /></button>
                  <button onClick={() => del(p)} className="hover:text-[#FF3B30]" data-testid={`admin-del-${p.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white border border-black/20 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" data-testid="admin-product-modal">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-3xl tracking-tighter">{editing.id ? "EDIT" : "NEW"} PRODUCT</h2>
              <button onClick={() => setEditing(null)}><X /></button>
            </div>
            <div className="space-y-4">
              <Field label="NAME" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="pf-name" />
              <div className="grid grid-cols-2 gap-3">
                <Select label="CATEGORY" value={editing.category} options={["tops","bottoms","footwear","accessories"]} onChange={(v) => setEditing({ ...editing, category: v })} />
                <Select label="GENDER" value={editing.gender} options={["men","women","unisex"]} onChange={(v) => setEditing({ ...editing, gender: v })} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="PRICE (₹)" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} testId="pf-price" />
                <Field label="MRP (₹)" type="number" value={editing.mrp} onChange={(v) => setEditing({ ...editing, mrp: v })} />
                <Field label="STOCK" type="number" value={editing.stock} onChange={(v) => setEditing({ ...editing, stock: v })} />
              </div>
              <Field label="FIT" value={editing.fit} onChange={(v) => setEditing({ ...editing, fit: v })} />
              <Field label="SIZES (comma)" value={editing.sizes.join(",")} onChange={(v) => setEditing({ ...editing, sizes: v.split(",").map(s => s.trim()) })} />
              <Field label="COLORS (comma)" value={editing.colors.join(",")} onChange={(v) => setEditing({ ...editing, colors: v.split(",").map(s => s.trim()) })} />
              <Field label="IMAGE URL #1" value={editing.images?.[0] || ""} onChange={(v) => setEditing({ ...editing, images: [v, ...(editing.images?.slice(1) || [])] })} />
              <Field label="IMAGE URL #2 (optional)" value={editing.images?.[1] || ""} onChange={(v) => setEditing({ ...editing, images: [editing.images?.[0] || "", v] })} />
              <Field label="TAGS (comma: new-drop, sale, collab, best-seller)" value={(editing.tags || []).join(",")} onChange={(v) => setEditing({ ...editing, tags: v.split(",").map(s => s.trim()).filter(Boolean) })} />
              <label className="block">
                <span className="font-display tracking-widest text-[10px] text-black/50">DESCRIPTION</span>
                <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} className="w-full bg-transparent border border-black/20 px-3 py-2 mt-1" />
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!editing.is_drop} onChange={(e) => setEditing({ ...editing, is_drop: e.target.checked })} className="accent-[#FF3B30]" />
                <span className="font-display tracking-widest text-xs">MARK AS DROP</span>
              </label>
              <div className="flex gap-3 pt-4">
                <button onClick={save} className="bg-[#FF3B30] text-white px-6 py-3 font-display tracking-widest text-xs hover:bg-black" data-testid="pf-save">SAVE</button>
                <button onClick={() => setEditing(null)} className="border border-black/30 px-6 py-3 font-display tracking-widest text-xs">CANCEL</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="font-display tracking-widest text-[10px] text-black/50">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border border-black/20 px-3 py-2 mt-1" data-testid={testId} />
    </label>
  );
}

function Select({ label, value, options, onChange }) {
  return (
    <label className="block">
      <span className="font-display tracking-widest text-[10px] text-black/50">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white border border-black/20 px-3 py-2 mt-1">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
